import { useRef, useState } from 'react';
import { ImagePlus, Trash2, Loader2 } from 'lucide-react';
import Sheet from '../../shared/ui/Sheet';
import {
  updateRestaurant,
  uploadRestaurantImage,
} from '../../shared/services/userService';
import { useAuth } from '../../contexts/AuthContext';
import ConfirmDialog from '../menu/ConfirmDialog';

// Mirrors iOS RestaurantBannerUploadView: 200pt banner preview, upload /
// change button, destructive remove button, uploading overlay.
export default function BannerSheet({ open, onClose }) {
  const { restaurant, refreshRestaurant } = useAuth();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [error, setError] = useState(null);

  const bannerURL = restaurant?.bannerURL || '';

  const onPick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f || !restaurant) return;
    setUploading(true);
    setError(null);
    try {
      await uploadRestaurantImage(restaurant.id, f, 'banner');
      await refreshRestaurant();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const onRemove = async () => {
    if (!restaurant) return;
    setUploading(true);
    setError(null);
    try {
      const fields = { bannerURL: null };
      if (restaurant.imageURL === restaurant.bannerURL) fields.imageURL = null;
      await updateRestaurant(restaurant.id, fields);
      await refreshRestaurant();
      setConfirmRemove(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title="Restaurant Banner">
      <div className="px-4 pb-8 space-y-4">
        {bannerURL ? (
          <img
            src={bannerURL}
            alt="Restaurant banner"
            className="w-full h-[200px] object-cover rounded-xl border border-gray-200"
          />
        ) : (
          <div className="h-[200px] rounded-xl bg-gray-100 border border-gray-200 flex flex-col items-center justify-center gap-2 text-gray-400">
            <ImagePlus size={40} />
            <p className="text-sm font-semibold">No Banner Image</p>
          </div>
        )}

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onPick}
        />
        <button
          type="button"
          disabled={uploading}
          onClick={() => fileRef.current?.click()}
          className="w-full py-3 rounded-xl bg-[#FF6B35] text-white text-sm font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-50"
        >
          {uploading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <ImagePlus size={16} />
          )}
          {bannerURL ? 'Change Banner' : 'Upload Banner'}
        </button>

        {bannerURL && (
          <button
            type="button"
            disabled={uploading}
            onClick={() => setConfirmRemove(true)}
            className="w-full py-3 rounded-xl bg-red-50 text-red-500 text-sm font-bold flex items-center justify-center gap-2 active:bg-red-100 disabled:opacity-50"
          >
            <Trash2 size={16} />
            Remove Banner
          </button>
        )}

        {error && (
          <p className="text-xs text-red-500 font-medium text-center">{error}</p>
        )}
      </div>

      <ConfirmDialog
        open={confirmRemove}
        title="Remove Banner"
        message="Remove your restaurant banner image?"
        confirmLabel="Remove"
        busy={uploading}
        onConfirm={onRemove}
        onCancel={() => setConfirmRemove(false)}
      />
    </Sheet>
  );
}
